import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    IEmailTemplate,
    IEmailTemplateLabel,
    IEmailTemplatesFilters,
    ICreateEmailTemplatePayload,
    IPreviewTemplatePayload,
    IPreviewTemplateResponse,
    IUpdateEmailTemplatePayload,
} from '../../../common';

interface ISetTemplatesDataPayload {
    templates: Array<IEmailTemplate>;
    totalPages: number;
    totalCount: number;
    currentPage: number;
}

interface IEmailTemplatesPickerState {
    options: Array<IEmailTemplateLabel>;
    rendered: IPreviewTemplateResponse | null;
    isLoading: boolean;
    isApplying: boolean;
}

interface IEmailTemplatesSlice {
    templates: Array<IEmailTemplate>;
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalCount: number;
    search: string;
    filters: IEmailTemplatesFilters;
    editorOpen: boolean;
    editorTemplate: IEmailTemplate | null;
    picker: IEmailTemplatesPickerState;
    uiState: {
        isLoadingTemplates: boolean;
        isSavingTemplate: boolean;
        isDeletingTemplate: boolean;
    };
}

const initialState: IEmailTemplatesSlice = {
    templates: [],
    currentPage: 1,
    pageSize: 3,
    totalPages: 1,
    totalCount: 0,
    search: '',
    filters: {
        status: null,
        type: null,
    },
    editorOpen: false,
    editorTemplate: null,
    picker: {
        options: [],
        rendered: null,
        isLoading: false,
        isApplying: false,
    },
    uiState: {
        isLoadingTemplates: true,
        isSavingTemplate: false,
        isDeletingTemplate: false,
    },
};

export const sliceName = 'emailTemplates';
export const emailTemplatesSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        fetchTemplates: (state) => {
            state.uiState.isLoadingTemplates = true;
        },
        setTemplatesData: (
            state,
            action: PayloadAction<ISetTemplatesDataPayload>
        ) => {
            state.templates = action.payload.templates;
            state.totalPages = action.payload.totalPages;
            state.totalCount = action.payload.totalCount;
            state.currentPage = action.payload.currentPage;
            state.uiState.isLoadingTemplates = false;
        },
        setTemplatesError: (state) => {
            state.uiState.isLoadingTemplates = false;
        },
        createTemplate: (
            state,
            _action: PayloadAction<{
                template: ICreateEmailTemplatePayload;
                publish?: boolean;
            }>
        ) => {
            state.uiState.isSavingTemplate = true;
        },
        updateTemplate: (
            state,
            _action: PayloadAction<{
                template: IUpdateEmailTemplatePayload;
                publish?: boolean;
            }>
        ) => {
            state.uiState.isSavingTemplate = true;
        },
        setSaveTemplateDone: (state) => {
            state.uiState.isSavingTemplate = false;
        },
        deleteTemplate: (state, _action: PayloadAction<number>) => {
            state.uiState.isDeletingTemplate = true;
        },
        setDeleteTemplateDone: (state) => {
            state.uiState.isDeletingTemplate = false;
        },
        openEditor: (
            state,
            action: PayloadAction<IEmailTemplate | null>
        ) => {
            state.editorOpen = true;
            state.editorTemplate = action.payload;
        },
        closeEditor: (state) => {
            state.editorOpen = false;
            state.editorTemplate = null;
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },
        setSearch: (state, action: PayloadAction<string>) => {
            state.search = action.payload;
            state.currentPage = 1;
        },
        setFilters: (
            state,
            action: PayloadAction<Partial<IEmailTemplatesFilters>>
        ) => {
            state.filters = { ...state.filters, ...action.payload };
            state.currentPage = 1;
        },
        fetchPickerOptions: (state) => {
            state.picker.isLoading = true;
        },
        setPickerOptions: (
            state,
            action: PayloadAction<Array<IEmailTemplateLabel>>
        ) => {
            state.picker.options = action.payload;
            state.picker.isLoading = false;
        },
        setPickerOptionsError: (state) => {
            state.picker.isLoading = false;
        },
        applyTemplate: (
            state,
            _action: PayloadAction<IPreviewTemplatePayload>
        ) => {
            state.picker.isApplying = true;
        },
        setRenderedTemplate: (
            state,
            action: PayloadAction<IPreviewTemplateResponse>
        ) => {
            state.picker.rendered = action.payload;
            state.picker.isApplying = false;
        },
        setApplyTemplateError: (state) => {
            state.picker.isApplying = false;
        },
        clearPicker: (state) => {
            state.picker.rendered = null;
            state.picker.isApplying = false;
        },
    },
});

export const emailTemplatesActions = emailTemplatesSlice.actions;
export default emailTemplatesSlice.reducer;
